import { useState, useEffect, useCallback, useMemo } from 'react';
import { type ProveedorRequest, type ProveedorFormErrors } from '../types/proveedor';
import { ProveedorValidationService } from '../services/proveedorValidation';

export interface UseProveedorFormProps {
  initialProveedorRequest: ProveedorRequest | null;
  isOpen: boolean;
  onSubmit: (proveedor: ProveedorRequest) => void;
}

export interface UseProveedorFormReturn {
  formData: ProveedorRequest | null;
  errors: ProveedorFormErrors;
  isValid: boolean;
  isDirty: boolean;
  isSubmitting: boolean;
  handleFieldChange: (field: keyof ProveedorRequest, value: unknown) => void;
  handleSubmit: () => Promise<void>;
  resetForm: () => void;
  validateForm: () => boolean;
}

export const useProveedorForm = ({
  initialProveedorRequest,
  isOpen,
  onSubmit,
}: UseProveedorFormProps): UseProveedorFormReturn => {
  const [formData, setFormData] = useState<ProveedorRequest | null>(null);
  const [errors, setErrors] = useState<ProveedorFormErrors>({});
  const [isDirty, setIsDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = useCallback(() => {
    setFormData(null);
    setErrors({});
    setIsDirty(false);
    setIsSubmitting(false);
  }, []);

  // Reset form when dialog opens/closes or proveedor changes
  useEffect(() => {
    if (isOpen) {
      if (initialProveedorRequest) {
        setFormData({ ...initialProveedorRequest });
      } else {
        // Create empty form for new proveedor
        setFormData({
          razonSocial: '',
          nombreFantasia: '',
          personaContacto: '',
          idTipoIdentificacion: 0,
          identificacion: '',
          idGenero: 0,
          idEstado: 1,
          idGrupo: undefined,
          idAsociacion: undefined,
          direccion: '',
          correoContacto: '',
          telefono: '',
        });
      }
      setErrors({});
      setIsDirty(false);
      setIsSubmitting(false);
    } else {
      resetForm();
    }
  }, [initialProveedorRequest, isOpen, resetForm]);

  const validateForm = useCallback((): boolean => {
    if (!formData) return false;

    const validationErrors = ProveedorValidationService.validateProveedor(formData);
    setErrors(validationErrors);

    return ProveedorValidationService.isFormValid(validationErrors);
  }, [formData]);

  const handleFieldChange = useCallback((field: keyof ProveedorRequest, value: unknown) => {
    setFormData(current => {
      if (!current) return null;

      const updated = {
        ...current,
        [field]: value,
      };

      // Mark form as dirty
      if (!isDirty) {
        setIsDirty(true);
      }

      // Re-validate the specific field
      const fieldRules = ProveedorValidationService.getValidationRules()[field];
      if (fieldRules) {
        const fieldError = ProveedorValidationService.validateField(value, fieldRules);

        setErrors(currentErrors => {
          const newErrors = { ...currentErrors };

          if (fieldError) {
            // Set the error if it exists
            newErrors[field as keyof ProveedorFormErrors] = fieldError;
          } else {
            // Remove the error property completely if field is valid
            delete newErrors[field as keyof ProveedorFormErrors];
          }

          return newErrors;
        });
      }

      return updated;
    });
  }, [isDirty]);

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (!formData || isSubmitting) return;

    setIsSubmitting(true);

    try {
      const isFormValid = validateForm();
      if (isFormValid) {
        await onSubmit(formData);
        resetForm();
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      // In a real app, you might want to show a toast or set a global error state
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, isSubmitting, validateForm, onSubmit, resetForm]);

  const isValid = useMemo(() => {
    return formData ? ProveedorValidationService.isFormValid(errors) : false;
  }, [formData, errors]);

  return {
    formData,
    errors,
    isValid,
    isDirty,
    isSubmitting,
    handleFieldChange,
    handleSubmit,
    resetForm,
    validateForm,
  };
};